import { useMemo, useState } from 'react';
import { usePhoneCrumb } from '../lib/usePhoneCrumb';
import { useNavigate } from 'react-router-dom';
import {
  Card, Chip, DataTable, EmptyState, Pane, PaneHead, StatCard, type TableRow,
} from '@/components/kit';
import { useShellChrome } from '@/layouts/shellChrome';
import { usePageMeta } from '@/hooks/usePageMeta';
import { stockTotals, useStockRows } from '../lib/useStockReport';
import { exportCsv } from '@/pages/roles/lib/exportCsv';
import { Bar } from '../components/Charts';
import { money, num } from '../lib/scope';
import { ManagerSurface } from '../components/ManagerSurface';
import { ManagerMenu } from '../components/ManagerMenu';

/**
 * Clinic manager · Stock & consumption — the sixth door on `g_r1` / `g_reports`
 * ("value EGP 38k · 2 batches expiring · top-consumed").
 *
 * THE SHELF IS NOW, NOT A MONTH. Unlike the other five reports this one has no
 * month picker: what is on the shelf is a present-tense fact, and the use rate
 * the server returns is already a measured monthly figure (90 days of
 * dispensing). Value is quantity × what the clinic last paid, falling back to
 * the expected cost the stock manager set on the item.
 *
 * Read-only on purpose — the manager reads the shelf here; the stock manager
 * moves it on /stock.
 */
export function ManagerStockPage() {
  const go = useNavigate();
  const [query, setQuery] = useState('');
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const { rows, loading: shelfLoading } = useStockRows();

  usePageMeta({ title: 'Stock & consumption' });

  const { totalValue, totalBurn } = stockTotals(rows);
  const unpriced = rows.filter((r) => r.unitCost === null && r.inStock > 0).length;
  const low = rows.filter((r) => r.low).length;
  const expiring = rows.filter((r) => r.expiringSoon).length;
  const topBurn = rows.filter((r) => (r.burn ?? 0) > 0).slice(0, 5);

  const shown = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return rows;
    return rows.filter((r) => `${r.name} ${r.category ?? ''}`.toLowerCase().includes(q));
  }, [rows, query]);

  const selected = selectedId ? rows.find((r) => r.id === selectedId) ?? null : null;

  const tableRows: TableRow[] = shown.map((r) => ({
    id: r.id,
    cells: {
      item: (
        <div>
          <p className="t-strong">{r.name}</p>
          <p className="t-cap mut">
            {[r.category, `${num(r.inStock)} ${r.unit ?? 'on the shelf'}`].filter(Boolean).join(' · ')}
          </p>
        </div>
      ),
      use: (
        <span className={r.perMonth === null ? 'mut' : 'num'}>
          {r.perMonth === null ? 'no use yet' : `${num(r.perMonth)}/mo`}
        </span>
      ),
      value: <span className="w6 num">{r.value === null ? '—' : money(r.value)}</span>,
      state: (
        <span className="flex" style={{ gap: 4, flexWrap: 'wrap' }}>
          {r.low ? <Chip kind="warn" small>low</Chip> : null}
          {r.expiringSoon ? <Chip kind="danger" small>expiring</Chip> : null}
          {!r.low && !r.expiringSoon ? <span className="t-cap mut">fine</span> : null}
        </span>
      ),
    },
  }));

  const crumb = usePhoneCrumb('Reports', 'Stock & consumption');
  useShellChrome(
    {
      raw: true,
      ...crumb,
      paneOpen: !!selected,
      onPaneClose: () => setSelectedId(null),
      phoneMenu: (close) => <ManagerMenu door="reports" onClose={close} />,
      title: 'Stock & consumption',
      sub: `the shelf today · ${num(rows.length)} item${rows.length === 1 ? '' : 's'}`,
      search: 'Search an item, a category…',
      searchValue: query,
      onSearchChange: setQuery,
      primary: {
        label: 'Export',
        onClick: () =>
          exportCsv('stock-consumption', ['Item', 'Category', 'On the shelf', 'Per month', 'Unit cost', 'Value'],
            rows.map((r) => [
              r.name, r.category ?? '', r.inStock, r.perMonth ?? '',
              r.unitCost ?? '', r.value === null ? '' : Math.round(r.value),
            ])),
      },
      pane: selected ? (
        <Pane
          head={
            <PaneHead
              overline="The item of the selected row"
              title={selected.name}
              sub={[selected.category, selected.unit].filter(Boolean).join(' · ') || 'on the shelf'}
            />
          }
          foot={
            <button type="button" className="btn btn-outline" style={{ flex: 1 }}
              onClick={() => go('/stock')}>
              Open the shelf ›
            </button>
          }
        >
          <Card>
            <p className="t-ovl mut">On the shelf</p>
            <p className="t-sec sec" style={{ marginTop: 4 }}>
              {num(selected.inStock)} {selected.unit ?? ''} · {selected.lots} batch{selected.lots === 1 ? '' : 'es'}
            </p>
            <p className="t-cap mut">
              {selected.value === null ? 'no price on it yet' : `worth ${money(selected.value)} at ${money(selected.unitCost ?? 0)} each`}
            </p>
          </Card>
          <Card>
            <p className="t-ovl mut">Use</p>
            <p className="t-sec sec" style={{ marginTop: 4 }}>
              {selected.perMonth === null ? 'nothing dispensed in 90 days' : `${num(selected.perMonth)} a month`}
            </p>
            {selected.burn !== null ? (
              <p className="t-cap mut">{money(selected.burn)} a month at today’s cost</p>
            ) : null}
            {selected.perMonth && selected.perMonth > 0 ? (
              <p className="t-cap mut">
                lasts about {num((selected.inStock / selected.perMonth) * 30)} days at this rate
              </p>
            ) : null}
          </Card>
          <Card>
            <p className="t-ovl mut">Next expiry</p>
            <p className={`t-sec ${selected.expiringSoon ? 'danger' : 'sec'}`} style={{ marginTop: 4 }}>
              {selected.nextExpiry ?? 'no dated batch'}
            </p>
          </Card>
        </Pane>
      ) : undefined,
    },
    [query, selectedId, rows.length, totalValue, totalBurn],
  );

  return (
    <div className="maie desk">
      <ManagerSurface />
      <div className="dcontent">
        <div className="kit-stats" style={{ display: 'flex', gap: 12, margin: '4px 0 20px' }}>
          <StatCard label="Value on the shelf" value={money(totalValue)} />
          <StatCard label="Used a month" value={money(totalBurn)} />
          <StatCard label="Running low" value={num(low)} tone={low ? 'warn' : undefined} />
          <StatCard label="Expiring soon" value={num(expiring)} tone={expiring ? 'warn' : undefined} />
        </div>

        <div className="cm-split" style={{ display: 'flex', gap: 20, alignItems: 'flex-start' }}>
          <div style={{ flex: 1.4, minWidth: 0 }}>
            <DataTable
              caption="Stock & consumption"
              columns={[
                { key: 'item', label: 'Item', width: 40 },
                { key: 'use', label: 'Use', width: 18 },
                { key: 'value', label: 'Value', width: 22, role: 'trailing' },
                { key: 'state', label: 'State', width: 20 },
              ]}
              rows={tableRows}
              selectedId={selectedId}
              onSelect={(r) => setSelectedId((cur) => (cur === r.id ? null : r.id))}
              empty={
                shelfLoading ? (
                  <EmptyState title="Reading the shelf…" />
                ) : query ? (
                  <EmptyState title="No item matches that" sub="Clear the search to see the whole shelf." />
                ) : (
                  <EmptyState title="The shelf is empty" sub="Items appear here once the stock manager adds them." />
                )
              }
            />
          </div>
          <div style={{ flex: 1, minWidth: 0 }}>
            <Card>
              <p className="t-ovl mut mb8">Top-consumed, by cost a month</p>
              {topBurn.length ? (
                topBurn.map((r) => (
                  <Bar key={r.id} label={r.name} value={r.burn ?? 0} max={topBurn[0].burn ?? 1}
                    display={money(r.burn ?? 0)} />
                ))
              ) : (
                <p className="t-sec mut">Nothing priced has been dispensed in the last 90 days.</p>
              )}
              {unpriced ? (
                <p className="t-cap mut">
                  {num(unpriced)} item{unpriced === 1 ? '' : 's'} on the shelf carr{unpriced === 1 ? 'ies' : 'y'} no price
                </p>
              ) : null}
            </Card>
          </div>
        </div>
      </div>
    </div>
  );
}
